import { z } from 'zod';

import {
  ReconciliationStatusSchema,
  type ReconciliationFilters,
  type ReconciliationStatus,
} from './types';

export const ReconciliationTabSchema = z.union([
  z.literal('all'),
  ReconciliationStatusSchema,
]);
export type ReconciliationTab = z.infer<typeof ReconciliationTabSchema>;

export const DEFAULT_RECONCILIATION_TAB: ReconciliationTab = 'all';
export const RECONCILIATION_PAGE_SIZE = 25;

/** Same convention as Sync Health: a stale bookmark lands on the default view. */
export const ReconciliationSearchSchema = z.object({
  status: ReconciliationTabSchema.optional().catch(undefined),
  branchId: z.string().min(1).optional().catch(undefined),
  search: z.string().optional().catch(undefined),
  page: z.coerce.number().int().min(1).optional().catch(undefined),
});
export type ReconciliationSearch = z.infer<typeof ReconciliationSearchSchema>;

export type ReconciliationExportFilters = Omit<
  ReconciliationFilters,
  'page' | 'pageSize'
>;

/** The list filters without paging: what the CSV export is scoped to. */
export function exportFiltersFromSearch(
  search: ReconciliationSearch
): ReconciliationExportFilters {
  const tab = search.status ?? DEFAULT_RECONCILIATION_TAB;
  const term = search.search?.trim();
  return {
    status: tab === 'all' ? undefined : tab,
    branchId: search.branchId ?? undefined,
    search: term ? term : undefined,
  };
}

export function reconciliationFiltersFromSearch(
  search: ReconciliationSearch
): ReconciliationFilters {
  return {
    ...exportFiltersFromSearch(search),
    page: search.page ?? 1,
    pageSize: RECONCILIATION_PAGE_SIZE,
  };
}

export const RECONCILIATION_EXPORT_PATH = '/v1/odoo/reconciliation/export.csv';

/** Relative to the api client's base URL, so auth and tenant headers ride along. */
export function reconciliationExportPath(
  filters: ReconciliationExportFilters
): string {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.branchId) params.set('branchId', filters.branchId);
  const term = filters.search?.trim();
  if (term) params.set('search', term);
  const query = params.toString();
  return query
    ? `${RECONCILIATION_EXPORT_PATH}?${query}`
    : RECONCILIATION_EXPORT_PATH;
}

export const reconciliationExportFilename = (
  filters: ReconciliationExportFilters,
  today: string
): string =>
  `odoo-reconciliation-${filters.status ? `${filters.status}-` : ''}${today}.csv`;

/**
 * A tile narrows the list to its status; clicking the tile already in force
 * goes back to every contract rather than doing nothing.
 */
export function tileSearch(
  status: ReconciliationStatus,
  current: ReconciliationSearch
): Pick<ReconciliationSearch, 'status' | 'page'> {
  const active = current.status === status;
  return { status: active ? 'all' : status, page: undefined };
}
